"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Search } from "lucide-react";
import { getDocsNav } from "@/data/docs-pages";

export default function DocsSidebar() {
  const pathname = usePathname();
  const [query, setQuery] = useState("");
  const sections = useMemo(() => getDocsNav(), []);

  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => item.label.toLowerCase().includes(needle)),
        }))
        .filter((section) => section.items.length > 0)
    : sections;

  return (
    <nav aria-label="Documentation" className="text-sm">
      <div className="relative mb-5">
        <Search
          size={14}
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search docs"
          aria-label="Search documentation"
          className="w-full rounded-lg border border-border-subtle bg-bg-elevated py-2 pl-8 pr-3 text-[13px] text-text-primary placeholder:text-text-muted focus:border-brand-purple-light focus:outline-none"
        />
      </div>

      <div className="flex flex-col gap-6">
        {filtered.map((section) => (
          <div key={section.title}>
            <p className="mb-2 font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-text-muted">
              {section.title}
            </p>
            <ul className="flex flex-col gap-0.5">
              {section.items.map((item) => {
                const active = pathname === item.href;
                return (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      aria-current={active ? "page" : undefined}
                      className={`block rounded-md px-2.5 py-1.5 leading-snug transition-colors ${
                        active
                          ? "bg-brand-purple-light/10 font-medium text-brand-purple-light"
                          : "text-text-secondary hover:bg-bg-elevated hover:text-text-primary"
                      }`}
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {needle && filtered.length === 0 && (
          <p className="text-text-muted">No pages match &ldquo;{query}&rdquo;.</p>
        )}
      </div>
    </nav>
  );
}
